import {
  Activity,
  CircleCheck,
  CircleDot,
  Hourglass,
  Info,
  PowerOff,
  Send,
} from "lucide-react";

import type { Pod, Pool, Replica, ReplicaStatus, Service } from "@/api/types";
import { Pill, type PillTone } from "@/components/Pill";
import { Section } from "@/components/Section";
import { humanCloudLabel } from "@/workloads/tree-data";

interface Props {
  pod: Pod | null;
  replica: Replica;
  pool: Pool;
  service: Service;
}

export function PodInfo({ pod, replica, pool, service }: Props) {
  return (
    <>
      <DrainTimeline replica={replica} />
      <Section title="Identity & links" icon={<Info />}>
      <div className="kv">
        <span className="k">service</span>
        <span className="v mono">{service.name}</span>
        <span className="k">pool</span>
        <span className="v mono">{pool.sky_service_name}</span>
        <span className="k">replica id</span>
        <span className="v mono num">{replica.id}</span>
        <span className="k">cluster</span>
        <span className="v mono">{replica.sky_cluster_name ?? "—"}</span>
        <span className="k">status</span>
        <span className="v">
          <Pill
            tone={
              replica.status === "ready"
                ? "ok"
                : replica.status === "failed" || replica.status === "not_ready"
                  ? "danger"
                  : "warn"
            }
          >
            {replica.status}
          </Pill>
        </span>
        <span className="k">node</span>
        <span className="v mono">
          {pod ? `${pod.node_role}#${pod.node_index}` : "single-node replica"}
        </span>
        <span className="k">internal_ip</span>
        <span className="v mono">{pod?.internal_ip ?? "—"}</span>
        <span className="k">external_ip</span>
        <span className="v mono">{pod?.external_ip ?? replica.external_ip ?? "—"}</span>
        <span className="k">external_port</span>
        <span className="v mono">{replica.external_port ?? "—"}</span>
        <span className="k">cloud</span>
        <span className="v mono">
          {replica.cloud ? humanCloudLabel(replica.cloud) : "—"}
        </span>
        <span className="k">region</span>
        <span className="v mono">{replica.region ?? "—"}</span>
        <span className="k">instance_type</span>
        <span className="v mono">{replica.instance_type ?? "—"}</span>
        <span className="k">started_at</span>
        <span className="v mono">
          {pod?.started_at
            ? new Date(pod.started_at).toLocaleString()
            : replica.started_at
              ? new Date(replica.started_at).toLocaleString()
              : "—"}
        </span>
        <span className="k">last_health_at</span>
        <span className="v mono">
          {replica.last_health_at
            ? new Date(replica.last_health_at).toLocaleString()
            : "—"}
        </span>
      </div>
    </Section>
    </>
  );
}

/**
 * Visual progress timeline of the graceful drain sequence:
 *
 *   ready → draining_long_lived → draining → shutting_down
 *           (EDS push)            (ACK rcvd) (drain wait)   (SkyServe down)
 *
 * The four phases come from
 * `controller/controller/draining/orchestrator.py` —
 * `drain_with_consistency` walks them in order and waits for the
 * Envoy ACK between (1) and (2) so traffic stops landing on this pod
 * before the SkyServe teardown fires. Surfacing it in the WebUI lets
 * operators see *which* step the orchestrator is currently waiting
 * on rather than guessing from a frozen "shutting_down" pill.
 */
function DrainTimeline({ replica }: { replica: Replica }) {
  const phase = drainPhase(replica.status);
  const steps: {
    id: typeof phase;
    label: string;
    icon: React.ReactNode;
  }[] = [
    { id: "ready", label: "ready", icon: <CircleCheck /> },
    { id: "ws-only", label: "EDS push (WS-only)", icon: <Send /> },
    { id: "draining", label: "ACK rcvd · drain wait", icon: <Hourglass /> },
    { id: "shutting", label: "SkyServe down", icon: <PowerOff /> },
  ];
  const currentIdx = steps.findIndex((s) => s.id === phase);
  return (
    <Section title="Drain timeline" icon={<Activity />}>
      <p className="muted" style={{ fontSize: 11.5, margin: "0 0 12px" }}>
        The drain orchestrator gates each transition on the previous
        Envoy ACK to prevent stale-endpoint races. The current phase is
        derived from <code>replica.status</code>; intermediate ACK /
        drain-wait timing lives in the controller events log.
      </p>
      <ol
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          display: "grid",
          gridTemplateColumns: `repeat(${steps.length}, 1fr)`,
          gap: 8,
        }}
      >
        {steps.map((s, idx) => {
          const isPast = idx < currentIdx;
          const isCurrent = idx === currentIdx;
          const tone: PillTone = isPast
            ? "ok"
            : isCurrent
              ? "info"
              : "neutral";
          return (
            <li
              key={s.id}
              style={{
                padding: "8px 10px",
                border: "1px solid var(--edge)",
                borderRadius: 6,
                background: isCurrent
                  ? "var(--surface-2)"
                  : "transparent",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 4,
                }}
              >
                {isPast ? <CircleCheck /> : isCurrent ? <CircleDot /> : s.icon}
                <span style={{ fontSize: 11.5, fontWeight: 600 }}>
                  {s.label}
                </span>
              </div>
              <Pill tone={tone}>
                {isPast ? "done" : isCurrent ? "in progress" : "pending"}
              </Pill>
            </li>
          );
        })}
      </ol>
    </Section>
  );
}

type DrainPhase = "ready" | "ws-only" | "draining" | "shutting";

function drainPhase(status: ReplicaStatus): DrainPhase {
  switch (status) {
    case "draining_long_lived":
      return "ws-only";
    case "draining":
      return "draining";
    case "shutting_down":
    case "failed":
      return "shutting";
    default:
      return "ready";
  }
}
